import { Flex, Tooltip, Typography } from "antd"
import cls from "classname"
import { useTranslation } from "react-i18next"

import { CircleInfoInfoCircleTooltipInformation, tooltipTitle } from "../helpers"

import "./styles.less"

const { Text } = Typography

// 37 is measured width of recommended block with tooltip,408 is width of scale
const recommendedBlockPercentage = (37 / 408) * 100
// 37 is measured width of minimum block with tooltip
const minimumBlockPercentage = (37 / 408) * 100
const criticalBlockPercentage = (36 / 408) * 100

const formatAmount = (value) =>
  `$${Number(value).toLocaleString("en-US", { minimumFractionDigits: 2 })}`

const BalanceScale = ({ balance, isMobile }) => {
  const { t } = useTranslation()
  const {
    min_amount,
    recommended,
    amount,
    calculated_recommended,
    calculated_min_amount,
    critical_level
  } = balance ?? {}
  //const current = 1000
  const maxValue =
    amount > calculated_recommended
      ? amount
      : calculated_recommended * (1 + +(calculated_min_amount / calculated_recommended)?.toFixed(2))

  const minPercent = (calculated_min_amount / maxValue) * 100
  const recPercent = (calculated_recommended / maxValue) * 100
  const criticalPercent = (critical_level / maxValue) * 100
  const currentPercent = (amount / maxValue) * 100
  const isLessSpaceForMin = minPercent < minimumBlockPercentage
  const criticalBlockPercent = criticalPercent
  const isEnoughSpaceForMinBlock =
    criticalBlockPercent + criticalBlockPercentage / 2 < minPercent - minimumBlockPercentage / 2
  const minBlockPercent = isEnoughSpaceForMinBlock
    ? minPercent
    : criticalBlockPercent + criticalBlockPercentage / 2 + minimumBlockPercentage / 2
  const isEnoughSpaceForRecBlock =
    minBlockPercent + minimumBlockPercentage / 2 < recPercent - recommendedBlockPercentage / 2
  // const recBlockPercent = recPercent + recommendedBlockPercentage > 100 ? recPercent - recommendedBlockPercentage : (recPercent + (isLessSpaceForMin ? minimumBlockPercentage : 0))
  const recBlockPercent = isEnoughSpaceForRecBlock
    ? recPercent
    : minBlockPercent + minimumBlockPercentage / 2 + recommendedBlockPercentage / 2

  const mobileLabels = isMobile ? (
    <div style={{ position: "relative", width: "100%", height: 18 }}>
      {!!critical_level && (
        <Flex
          gap={4}
          align="center"
          className="balance-scale__marker__labels"
          style={{ position: "absolute", left: 23, transform: "none" }}
        >
          <Text
            strong
            type={currentPercent < criticalPercent ? "danger" : "secondary"}
            style={{ fontSize: 12 }}
          >
            {t("Critical")}
          </Text>
          <CircleInfoInfoCircleTooltipInformation
            title={t(tooltipTitle["critical"])}
            isDanger={currentPercent < criticalPercent}
            overlayInnerStyle={{ width: 190 }}
          />
        </Flex>
      )}
      {!!calculated_min_amount && (
        <Flex
          gap={4}
          align="center"
          className="balance-scale__marker__labels"
          style={{ position: "absolute", left: 100, transform: "none" }}
        >
          <Text strong type="secondary" style={{ fontSize: 12 }}>
            {t("Minimum")}
          </Text>
          <CircleInfoInfoCircleTooltipInformation
            title={t(tooltipTitle["min"])}
            overlayInnerStyle={{ width: 190 }}
          />
        </Flex>
      )}
      {!!calculated_recommended && (
        <Flex
          gap={4}
          align="center"
          className="balance-scale__marker__labels"
          style={{ position: "absolute", right: 0, transform: "none" }}
        >
          <Text strong type="secondary" style={{ fontSize: 12 }}>
            {t("Recommended")}
          </Text>
          <CircleInfoInfoCircleTooltipInformation
            title={t(tooltipTitle["rec"])}
            placement="topLeft"
          />
        </Flex>
      )}
    </div>
  ) : (
    <div style={{ position: "relative", width: "408px", height: "18px", marginLeft: 24 }}>
      {!!critical_level && (
        <Flex
          gap={4}
          align="center"
          className={cls(
            "balance-scale__marker",
            "balance-scale__marker__labels",
            "balance-scale__marker__labels--min"
          )}
          style={{ left: `${criticalBlockPercent}%` }}
        >
          <Text
            strong
            type={currentPercent < criticalPercent ? "danger" : "secondary"}
            style={{ fontSize: 12 }}
          >
            {t("Crit")}
          </Text>
          <CircleInfoInfoCircleTooltipInformation
            title={t(tooltipTitle["critical"])}
            isDanger={currentPercent < criticalPercent}
            overlayInnerStyle={{ width: 190 }}
          />
        </Flex>
      )}
      {!!calculated_min_amount && (
        <Flex
          gap={4}
          align="center"
          className="balance-scale__marker balance-scale__marker__labels balance-scale__marker__labels--min"
          style={{ left: `${minBlockPercent}%` }}
        >
          <Text strong type="secondary" style={{ fontSize: 12 }}>
            {t("Min")}
          </Text>
          <CircleInfoInfoCircleTooltipInformation
            title={t(tooltipTitle["min"])}
            overlayInnerStyle={{ width: 190 }}
          />
        </Flex>
      )}
      {!!calculated_recommended && (
        <Flex
          gap={4}
          align="center"
          className="balance-scale__marker balance-scale__marker__labels balance-scale__marker__labels--rec"
          style={{ left: `${recBlockPercent}%` }}
        >
          <Text strong type="secondary" style={{ fontSize: 12 }}>
            {t("Rec")}
          </Text>
          <CircleInfoInfoCircleTooltipInformation
            title={t(tooltipTitle["rec"])}
            overlayInnerStyle={{ width: 220 }}
          />
        </Flex>
      )}
    </div>
  )

  return (
    <Flex gap={4} vertical className="balance-scale">
      {mobileLabels}
      <Flex gap={8} align="middle">
        <Text type="secondary" strong style={{ fontSize: 12, lineHeight: "16px" }}>
          $0
        </Text>

        <div className="balance-scale__bar">
          <div
            className={cls("balance-scale__track", {
              critical: currentPercent < criticalPercent
            })}
            style={{ width: `${currentPercent}%` }}
          />

          <div className="balance-scale__track--empty" />

          <div
            className={cls("balance-scale__marker", {
              "balance-scale__marker--base": currentPercent < minPercent,
              "balance-scale__marker--white": currentPercent > minPercent
            })}
            style={{ left: `${minPercent}%` }}
          />
          <div
            className={cls("balance-scale__marker", {
              "balance-scale__marker--base": currentPercent < recPercent,
              "balance-scale__marker--white": currentPercent > recPercent
            })}
            style={{ left: `${recPercent}%` }}
          />
          <div
            className={cls("balance-scale__marker", {
              "balance-scale__marker--base": currentPercent < criticalPercent,
              "balance-scale__marker--white": currentPercent > criticalPercent
            })}
            style={{ left: `${criticalPercent}%` }}
          />

          <Tooltip title={!isMobile ? t("Current") : null}>
            <div
              className={cls("balance-scale__marker", "balance-scale__marker--current", {
                critical: currentPercent < criticalPercent
              })}
              style={{ left: `${currentPercent > 0 ? currentPercent : 0}%` }}
            />
          </Tooltip>
        </div>
      </Flex>
    </Flex>
  )
}

export default BalanceScale
